import React from 'react';
import { Box, Chip, Stack, Typography } from '@mui/material';

export interface SuggestionValuePreviewProps {
    /** Raw `suggestedValue` from the AI prefill payload. May be plain text or a JSON-encoded structure. */
    value: string;
}

/**
 * The autofill service emits suggestions as opaque strings — sometimes plain
 * scalars ("RO", "Radu Gabriel"), sometimes JSON arrays of scalars
 * (`["sanitary","heating"]`), sometimes JSON arrays of objects (work
 * experiences, languages, language proficiency). Rendering them raw to the
 * worker is unfriendly — they see escaped JSON and bail. This component
 * sniffs the shape once and dispatches to the right tidy renderer.
 *
 * It deliberately stays in display-only territory: no inputs, no edit
 * affordances. The worker still confirms with the checkbox in the parent row.
 */
export const SuggestionValuePreview: React.FC<SuggestionValuePreviewProps> = ({ value }) => {
    if (value === null || value === undefined || value === '') {
        return <PlainCaption>—</PlainCaption>;
    }

    const parsed = tryParseJson(value);
    if (parsed === undefined) {
        // Not JSON — just a scalar like "Radu Gabriel" or "RO".
        return <PlainBody>{cleanScalar(value)}</PlainBody>;
    }

    if (Array.isArray(parsed)) {
        if (parsed.length === 0) {
            return <PlainCaption>—</PlainCaption>;
        }
        if (parsed.every((item) => isPrimitive(item))) {
            return (
                <Stack direction="row" spacing={0.5} flexWrap="wrap" rowGap={0.5}>
                    {parsed.map((item, i) => (
                        <Chip
                            key={i}
                            size="small"
                            label={cleanScalar(String(item))}
                            variant="outlined"
                        />
                    ))}
                </Stack>
            );
        }
        if (parsed.every((item) => isPlainObject(item))) {
            return (
                <Stack spacing={1}>
                    {parsed.map((item, i) => (
                        <ObjectCard key={i} obj={item} />
                    ))}
                </Stack>
            );
        }
        // Mixed array — keep things honest, render the raw JSON small.
        return <PlainCaption>{value}</PlainCaption>;
    }

    if (isPlainObject(parsed)) {
        return <ObjectCard obj={parsed} />;
    }

    // number, boolean, or string survived JSON.parse (e.g. `"true"`)
    return <PlainBody>{cleanScalar(String(parsed))}</PlainBody>;
};

const ObjectCard: React.FC<{ obj: Record<string, unknown> }> = ({ obj }) => {
    const entries = Object.entries(obj).filter(([, v]) => v !== null && v !== '' && v !== undefined);
    if (entries.length === 0) {
        return <PlainCaption>—</PlainCaption>;
    }
    return (
        <Box
            sx={{
                px: 1.25,
                py: 0.75,
                bgcolor: 'action.hover',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
            }}
        >
            {entries.map(([k, v]) => (
                <Stack
                    key={k}
                    direction="row"
                    spacing={1}
                    sx={{ py: 0.25, alignItems: 'flex-start' }}
                >
                    <Typography
                        variant="caption"
                        sx={{ fontWeight: 600, minWidth: 130, color: 'text.secondary' }}
                    >
                        {humanizeLabel(k)}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.primary', flex: 1 }}>
                        {formatLeafValue(v)}
                    </Typography>
                </Stack>
            ))}
        </Box>
    );
};

const PlainBody: React.FC<React.PropsWithChildren> = ({ children }) => (
    <Typography variant="body2" sx={{ color: 'text.primary' }}>
        {children}
    </Typography>
);

const PlainCaption: React.FC<React.PropsWithChildren> = ({ children }) => (
    <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
        {children}
    </Typography>
);

function tryParseJson(s: string): unknown | undefined {
    if (!s) return undefined;
    const t = s.trim();
    if (!(t.startsWith('{') || t.startsWith('['))) return undefined;
    try {
        return JSON.parse(s);
    } catch {
        return undefined;
    }
}

function isPrimitive(v: unknown): v is string | number | boolean {
    const t = typeof v;
    return t === 'string' || t === 'number' || t === 'boolean';
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
    return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/**
 * Snake_case / SCREAMING_CASE → Title Case. Used for object keys and
 * field-level labels. Leaves short codes (≤3 chars) alone so country codes
 * stay readable (`RO` should not become `Ro`).
 */
function humanizeLabel(s: string): string {
    if (!s) return s;
    if (s.length <= 3 && /^[A-Z]+$/.test(s)) return s;
    return s
        .toLowerCase()
        .replace(/[_-]+/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase())
        .trim();
}

/**
 * Tidy a SCREAMING_CASE token (e.g. `PLUMBER`, `EU_CITIZEN`) for the worker's
 * eye while leaving short codes (`RO`, `NL`) untouched and any string that
 * looks like a real value (mixed-case, proper noun, etc.) alone.
 */
function cleanScalar(s: string): string {
    if (!s) return s;
    if (s.length <= 3) return s;
    if (/^[A-Z][A-Z0-9_]*$/.test(s)) {
        return humanizeLabel(s);
    }
    return s;
}

function formatLeafValue(v: unknown): string {
    if (v === null || v === undefined) return '';
    if (typeof v === 'boolean') return v ? 'Yes' : 'No';
    if (typeof v === 'number') return String(v);
    if (typeof v === 'string') return cleanScalar(v);
    try {
        return JSON.stringify(v);
    } catch {
        return String(v);
    }
}

export const humanizeFieldLabel = humanizeLabel;
